import { useEffect, useRef, useState } from 'react';
import '../assets/styles/BarreProgression.css';

/**
 * Avancement d'une attente : import, lecture par l'IA, dépôt de fichiers.
 *
 * Deux usages :
 *  - `valeur` connue (ex. 3 fichiers sur 5) : la barre la montre telle quelle ;
 *  - `estimeeMs` : une seule requête dont on ne sait rien pendant qu'elle
 *    tourne — la barre avance doucement vers 90 % sur la durée annoncée, et
 *    n'atteint 100 % que quand `actif` repasse à faux. Le pourcentage est
 *    alors une estimation, dite comme telle par son libellé.
 */
export function useProgression(actif, { estimeeMs = 20000 } = {}) {
  const [valeur, setValeur] = useState(0);
  const debut = useRef(null);

  useEffect(() => {
    if (!actif) {
      // Fin de l'attente : on affiche 100 % un instant, puis on se retire.
      if (debut.current !== null) {
        setValeur(100);
        const fin = setTimeout(() => setValeur(0), 600);
        debut.current = null;
        return () => clearTimeout(fin);
      }
      return undefined;
    }
    debut.current = Date.now();
    setValeur(2);
    const minuteur = setInterval(() => {
      const ecoule = Date.now() - debut.current;
      // Courbe qui ralentit en approchant de 90 % : jamais bloquée, jamais finie.
      const p = 90 * (1 - Math.exp(-ecoule / (estimeeMs * 0.6)));
      setValeur(Math.max(2, Math.min(90, p)));
    }, 250);
    return () => clearInterval(minuteur);
  }, [actif, estimeeMs]);

  return valeur;
}

function BarreProgression({ valeur, libelle = null, visible = true, estimation = false }) {
  if (!visible || valeur <= 0) return null;
  const pct = Math.round(Math.max(0, Math.min(100, valeur)));
  return (
    <div className="progression" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="progression-tete">
        <span className="progression-libelle">{libelle || 'En cours…'}</span>
        <span className="progression-pct">
          {pct} %{estimation && pct < 100 ? ' (estimé)' : ''}
        </span>
      </div>
      <div className="progression-piste">
        <div className="progression-barre" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default BarreProgression;
